import React from 'react';
import { Button, Dialog, Flex, Text } from '@/components/ui';
import { IconCheck, IconX } from '@tabler/icons-react';

// Types
import { ColumnMapping } from './types';

interface ColumnSelectionModalProps {
  metadataType: string;
  metadataLabel: string;
  availableColumns: Array<{
    name: string;
    isAlreadyMapped: boolean;
    currentMapping?: ColumnMapping;
  }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onColumnSelect: (columnName: string, applyToAll?: boolean) => void;
  onRemoveMapping?: () => void; // For already mapped metadata
  currentMappedColumn?: string; // Currently mapped column name
  similarFilesCount?: number; // Number of other files with the same column
}

export const ColumnSelectionModal: React.FC<ColumnSelectionModalProps> = ({
  metadataType,
  metadataLabel,
  availableColumns,
  open,
  onOpenChange,
  onColumnSelect,
  onRemoveMapping,
  currentMappedColumn,
  similarFilesCount = 0,
}) => {
  void metadataType;
  const [showBulkConfirm, setShowBulkConfirm] = React.useState(false);
  const [pendingColumnSelection, setPendingColumnSelection] = React.useState<
    string | null
  >(null);
  const handleColumnClick = (columnName: string) => {
    if (similarFilesCount > 0) {
      // Show bulk confirmation dialog
      setPendingColumnSelection(columnName);
      setShowBulkConfirm(true);
    } else {
      // Apply only to this file
      onColumnSelect(columnName, false);
      onOpenChange(false);
    }
  };

  const handleBulkConfirm = (applyToAll: boolean) => {
    if (pendingColumnSelection) {
      onColumnSelect(pendingColumnSelection, applyToAll);
      onOpenChange(false);
      setShowBulkConfirm(false);
      setPendingColumnSelection(null);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: '400px' }}>
        <Dialog.Title>
          <Flex align="center" justify="between" width="100%">
            <Text size="4" weight="bold">
              Select Column for {metadataLabel}
            </Text>

            <Button
              variant="surface"
              size="medium"
              onClick={() => onOpenChange(false)}
              title="Close"
            >
              <IconX />
            </Button>
          </Flex>
        </Dialog.Title>

        <Dialog.Description>
          <Text size="2" color="gray">
            {currentMappedColumn
              ? `Currently mapped to "${currentMappedColumn}". Choose a different column or remove the mapping.`
              : `Choose a column to map as "${metadataLabel}".`}
          </Text>
        </Dialog.Description>

        {/* Show current mapping if exists */}
        {currentMappedColumn && (
          <Flex direction="column" gap="2" mt="4">
            <Text size="2" weight="medium">
              Current Mapping:
            </Text>
            <Button
              variant="soft"
              size="large"
              disabled
              style={{
                backgroundColor: 'var(--blue-3)',
                cursor: 'default',
              }}
            >
              <Flex align="center" gap="2">
                <Text size="2">{currentMappedColumn}</Text>
                <Text size="1" color="gray">
                  (Currently mapped)
                </Text>
              </Flex>
            </Button>
            {onRemoveMapping && (
              <Button
                variant="outlined"
                size="large"
                color="error"
                onClick={() => {
                  onRemoveMapping();
                  onOpenChange(false);
                }}
              >
                Remove Mapping
              </Button>
            )}
          </Flex>
        )}

        <Flex
          direction="column"
          gap="2"
          mt="4"
          style={{ maxHeight: '300px', overflowY: 'auto' }}
        >
          <Text size="2" weight="medium">
            {currentMappedColumn
              ? 'Choose Different Column:'
              : 'Available Columns:'}
          </Text>

          {availableColumns.map(column => {
            const isCurrentMapping = column.name === currentMappedColumn;
            // Allow selecting only if not mapped at all, or if it's the current mapping (for re-selection)
            // One metadata type can only map to one column (1:1 mapping)
            const canSelect = !column.isAlreadyMapped || isCurrentMapping;

            return (
              <Button
                key={column.name}
                variant={
                  isCurrentMapping
                    ? 'soft'
                    : column.isAlreadyMapped
                      ? 'soft'
                      : 'outlined'
                }
                size="large"
                onClick={() => canSelect && handleColumnClick(column.name)}
                disabled={!canSelect}
                style={{
                  justifyContent: 'space-between',
                  opacity: !canSelect ? 0.6 : 1,
                  backgroundColor: isCurrentMapping
                    ? 'var(--blue-3)'
                    : undefined,
                }}
              >
                <Flex align="center" gap="2">
                  <Text size="2">{column.name}</Text>
                  {isCurrentMapping && (
                    <Text size="1" color="blue">
                      (Current)
                    </Text>
                  )}
                  {column.isAlreadyMapped && !isCurrentMapping && (
                    <Text size="1" color="gray">
                      (
                      {column.currentMapping?.mappingType === 'metadata'
                        ? `Mapped as ${column.currentMapping.metadataType?.label}`
                        : column.currentMapping?.mappingType === 'formField'
                          ? `Form field: ${column.currentMapping.targetField}`
                          : 'Already mapped'}
                      )
                    </Text>
                  )}
                </Flex>
                {canSelect && !isCurrentMapping && (
                  <IconCheck color="var(--green-9)" />
                )}
              </Button>
            );
          })}

          {availableColumns.length === 0 && (
            <Text
              size="2"
              color="gray"
              style={{ textAlign: 'center', padding: '20px' }}
            >
              No available columns found.
            </Text>
          )}
        </Flex>
      </Dialog.Content>

      {/* Bulk Mapping Confirmation Dialog */}
      <Dialog.Root open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <Dialog.Content style={{ maxWidth: '400px' }}>
          <Dialog.Title>
            <Flex align="center" justify="between" width="100%">
              <Text size="4" weight="bold">
                Apply Mapping
              </Text>
              <Button
                variant="surface"
                size="medium"
                onClick={() => setShowBulkConfirm(false)}
                title="Close"
              >
                <IconX />
              </Button>
            </Flex>
          </Dialog.Title>

          <Dialog.Description>
            <Text size="2" color="gray">
              Found {similarFilesCount} other file
              {similarFilesCount === 1 ? '' : 's'} with column "
              {pendingColumnSelection}". How would you like to apply this
              mapping?
            </Text>
          </Dialog.Description>

          <Flex direction="column" gap="3" mt="4">
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleBulkConfirm(false)}
            >
              Apply only to this file
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleBulkConfirm(true)}
            >
              Apply to all {similarFilesCount + 1} files
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Dialog.Root>
  );
};

export default ColumnSelectionModal;
